import type { ContractAiDraftInput, ContractAiDraftSummary } from "@closer/shared";
import {
  AI_DRAFT_HUMAN_APPROVAL_REQUIRED_MESSAGE,
  CONTRACT_AI_DRAFT_DETAIL_LEVEL_NAMES,
  CONTRACT_AI_DRAFT_DETAIL_LEVELS,
  CONTRACT_AI_DRAFT_ERROR_MESSAGES,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  InfoBanner,
  Input,
  Select,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * AI İLE SÖZLEŞME TASLAĞI SİHİRBAZI (CLOS-239 `[187]` · CLOS-995 [S03]).
 *
 * ### ⚠ BU EKRAN METİN EDİTÖRÜ AÇMIYOR — düzenleme KABULDEN SONRA
 *
 * CLOS-995 *"kullanıcı düzenleyebilir"* diyor; CLOS-239'un kabul kriteri o düzenlemenin
 * NEREDE olacağını da söylüyor: *"düzenlenebilir çalışma içeriği CLOS-118 üzerinden ...
 * oluşturulur."* Sunucunun kabul ucu (`POST /ai-drafts/:id/accept`) gövdesinde YALNIZ
 * `confirmed: true` alıyor; sözleşmeyi SAKLANAN taslak çıktısından üretiyor.
 *
 * Bu yüzden burada bir metin editörü açsaydık kullanıcının yazdığı her değişiklik kabul
 * anında sessizce düşerdi — kullanıcı düzenlediğini sandığı metnin başka bir hâlini
 * imzalamaya giderdi. Ekran bunun yerine metni okunur gösteriyor, kabul ediyor ve
 * kanonik editöre (`/metin`) yönlendiriyor; düzenleme orada, kendi eşzamanlılık ve
 * denetim kurallarıyla yapılıyor. Sihirbaza editör eklemek AYRI bir sunucu ucu (taslak
 * güncelleme) ister ve o uç bugün kartsız.
 *
 * ### ⚠ "YENİDEN ÜRET" ÖNCEKİNİ EZMİYOR — VARYANT LİSTESİ
 *
 * Kart: *"Regenerate mevcut düzenlenmiş taslağı sessizce ezmez; gerekirse yeni
 * varyant/confirmation."* Sunucu her üretimde YENİ satır yazıyor (eski satır duruyor);
 * ekran da öyle davranıyor: üretilen her taslak bu oturumun varyant listesine giriyor,
 * kullanıcı aralarında geçiş yapabiliyor. Ekranda bir taslak dururken "Yeniden üret"
 * ÖNCE ONAY SORUYOR — çünkü yeni üretim KREDİ harcıyor ve kart açık onay istiyor.
 *
 * ### ⚠ KABUL: SUNUCU CEVABI GELMEDEN "SÖZLEŞME OLUŞTU" DENMİYOR
 *
 * Kart: *"server başarısı gelmeden sözleşme oluşturuldu gösterilmez"* ve *"CTA duplicate
 * submit'e kapanır."* Yönlendirme `acceptedContractId` ile birlikte geliyor; düğme
 * istek boyunca kapalı. İyimser bir yönlendirme, kapasite/lisans kaybında (CLOS-994)
 * kullanıcıyı var olmayan bir sözleşmenin sayfasına atardı.
 *
 * ### ⚠ HATA KABUL DÜĞMESİNİN YANINDA, yalnız toast'ta değil
 *
 * Kabul anında kaybedilen yetki/kapasite/lisans kullanıcının DÜZELTEBİLECEĞİ bir durum
 * (paket yükseltme, yetki isteme). Kaybolan bir toast'la söylenseydi kullanıcı yalnız
 * "olmadı"yı görürdü; sunucunun cümlesi ekranda kalıyor.
 */

const BOS_GIRDI: ContractAiDraftInput = {
  purpose: "",
  contractType: "",
  language: "tr",
  clientName: "",
  counterpartyName: "",
  additionalNotes: "",
  detailLevel: "standart",
  tone: "",
  standardClauses: [],
};

export function ContractAiDraftPage() {
  const { activeTenantId, activeTenant, can } = useAuth();
  const tenantId = activeTenantId!;
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [girdi, setGirdi] = useState<ContractAiDraftInput>(BOS_GIRDI);
  const [maddeMetni, setMaddeMetni] = useState("");
  /** Bu oturumda üretilen taslaklar — YENİSİ ESKİYİ SİLMİYOR (varyant geçmişi). */
  const [varyantlar, setVaryantlar] = useState<ContractAiDraftSummary[]>([]);
  const [acikId, setAcikId] = useState<string | null>(null);
  const [yenidenUretimOnayi, setYenidenUretimOnayi] = useState(false);
  const [okundu, setOkundu] = useState(false);
  const [kabulHatasi, setKabulHatasi] = useState<string | null>(null);
  /**
   * ⚠ ÜRETİM HATASI EKRANDA KALIYOR, yalnız toast'ta değil. "AI kredi hakkınız yok" ve
   * "AI motoru yapılandırılmamış" kalıcı DURUMLAR: kullanıcı paket yükseltmeli ya da
   * yöneticisine gitmeli. Üç saniyede silinen bir cümle o kararı vermesine yetmez —
   * kart da bu ikisini ayrı state sayıyor (CLOS-995: quota/restricted).
   */
  const [uretimHatasi, setUretimHatasi] = useState<string | null>(null);
  const [alanHatalari, setAlanHatalari] = useState<Record<string, string>>({});
  const sonucRef = useRef<HTMLHeadingElement>(null);

  const acik = varyantlar.find((v) => v.id === acikId) ?? null;

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const uret = useMutation({
    mutationFn: async () =>
      (
        await api<ContractAiDraftSummary>(`/api/contracts/ai-drafts`, {
          method: "POST",
          tenantId,
          body: JSON.stringify(girdi),
          /**
           * ⚠ Her TIKLAMA yeni anahtar (analiz ekranıyla aynı gerekçe): yalnız gerçek bir
           * ağ tekrar denemesi aynı anahtarı taşır ve sunucuda önbellekten karşılanır —
           * ikinci bir tıklama ikinci bir taslak ve ikinci bir kredi demektir.
           */
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: async (taslak) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      setAlanHatalari({});
      setUretimHatasi(null);
      setYenidenUretimOnayi(false);
      setOkundu(false);
      setKabulHatasi(null);
      /** ⚠ Kredi harcandı: abonelik sayacı bayat kalmasın (analiz ekranıyla aynı karar). */
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
      setVaryantlar((öncekiler) => [taslak, ...öncekiler]);
      setAcikId(taslak.id);
      /** Odak sonuca taşınıyor: ekran okuyucu kullanıcısı formun sonunda kalmasın. */
      requestAnimationFrame(() => sonucRef.current?.focus());
    },
    onError: (hata: unknown) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        const yeni: Record<string, string> = {};
        for (const alan of hata.fields) yeni[alan.field] = alan.message;
        setAlanHatalari(yeni);
        return;
      }
      setUretimHatasi(hata instanceof Error ? hata.message : "Taslak üretilemedi");
      /**
       * ⚠ Mesaj SUNUCUDAN: kredi hakkı yok, motor kapalı ve sağlayıcı düştü ayrı
       * cümleler; tek bir "taslak üretilemedi" kullanıcıya ne yapacağını söylemezdi.
       */
      toast.error(hata instanceof Error ? hata.message : "Taslak üretilemedi");
    },
  });

  const kabulEt = useMutation({
    mutationFn: async (draftId: string) =>
      (
        await api<ContractAiDraftSummary>(`/api/contracts/ai-drafts/${draftId}/accept`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ confirmed: true }),
        })
      ).data,
    onSuccess: async (taslak) => {
      /**
       * ⚠ Sözleşme ANCAK sunucu kimliği döndürdüyse var. Kimlik yoksa yönlendirme yok:
       * kullanıcıyı var olmayan bir sayfaya atmak, "oluştu" demenin en kötü biçimi.
       */
      if (!taslak.acceptedContractId) {
        setKabulHatasi("Sözleşme oluşturulamadı; taslak kabul edilmedi.");
        return;
      }

      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contracts") });
      toast.success("Sözleşme oluşturuldu");
      void navigate(`/app/sozlesmeler/${taslak.acceptedContractId}/metin`);
    },
    onError: (hata: unknown) =>
      setKabulHatasi(hata instanceof Error ? hata.message : "Taslak kabul edilemedi"),
  });

  /** ⚠ Sunucu kapısı `contract.create`; ekran onu ÖNDEN söylüyor (403'ü beklemiyor). */
  if (!can("contract.create")) {
    return (
      <InfoBanner tone="warning">
        Bu çalışma alanında sözleşme oluşturma yetkiniz bulunmuyor.
      </InfoBanner>
    );
  }

  const zorunluEksik = girdi.purpose.trim() === "" || girdi.contractType.trim() === "";
  const uretimSuruyor = uret.isPending;

  function maddeEkle() {
    const ad = maddeMetni.trim();
    if (ad === "") return;
    setGirdi((ö) => ({ ...ö, standardClauses: [...ö.standardClauses, ad] }));
    setMaddeMetni("");
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-bold text-fg-heading sm:text-3xl">AI ile taslak</h1>
        {/* ⚠ Vazgeç: hiçbir sözleşme oluşmadığı için geri dönmek yeterli. */}
        <Link className="text-sm font-medium text-primary hover:underline" to="/app/sozlesmeler">
          Vazgeç
        </Link>
      </header>

      <AiAdvisoryNotice />

      <InfoBanner>
        Taslak, mevcut çalışma alanınız olan{" "}
        <span className="font-semibold text-fg-heading">{activeTenant?.name ?? "—"}</span> içinde
        üretilir. Taslak tek başına sözleşme değildir; siz kabul edene kadar kayıt oluşmaz.
      </InfoBanner>

      <Card title="Girdi bilgileri">
        <div className="flex flex-col gap-3">
          <FormField label="Sözleşmenin amacı" error={alanHatalari.purpose} required>
            <Textarea
              rows={3}
              value={girdi.purpose}
              onChange={(e) => setGirdi((ö) => ({ ...ö, purpose: e.target.value }))}
              placeholder="Örn. İki şirket arasında danışmanlık hizmeti alınması."
            />
          </FormField>

          <FormField label="Sözleşme türü" error={alanHatalari.contractType} required>
            <Input
              value={girdi.contractType}
              onChange={(e) => setGirdi((ö) => ({ ...ö, contractType: e.target.value }))}
              placeholder="Örn. Danışmanlık Sözleşmesi"
            />
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Müvekkil">
              <Input
                value={girdi.clientName}
                onChange={(e) => setGirdi((ö) => ({ ...ö, clientName: e.target.value }))}
              />
            </FormField>
            <FormField label="Karşı taraf">
              <Input
                value={girdi.counterpartyName}
                onChange={(e) => setGirdi((ö) => ({ ...ö, counterpartyName: e.target.value }))}
              />
            </FormField>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Detay seviyesi">
              <Select
                value={girdi.detailLevel}
                onChange={(e) =>
                  setGirdi((ö) => ({
                    ...ö,
                    detailLevel: e.target.value as ContractAiDraftInput["detailLevel"],
                  }))
                }
              >
                {CONTRACT_AI_DRAFT_DETAIL_LEVELS.map((seviye) => (
                  <option key={seviye} value={seviye}>
                    {CONTRACT_AI_DRAFT_DETAIL_LEVEL_NAMES[seviye]}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Üslup">
              <Input
                value={girdi.tone}
                onChange={(e) => setGirdi((ö) => ({ ...ö, tone: e.target.value }))}
                placeholder="Örn. resmî ve kısa"
              />
            </FormField>
          </div>

          {/*
            ⚠ DİL ALANI GÖRÜNÜR ama tek seçenekli: şema notu açık — ürün bugün yalnız
            Türkçe üretiyor; ekran bunu SESSİZCE varsaymıyor.
          */}
          <FormField label="Taslak dili" hint="Taslaklar şu an yalnız Türkçe üretilmektedir.">
            <Select value={girdi.language} disabled onChange={() => {}}>
              <option value="tr">Türkçe</option>
            </Select>
          </FormField>

          <FormField label="Ek notlar">
            <Textarea
              rows={3}
              value={girdi.additionalNotes}
              onChange={(e) => setGirdi((ö) => ({ ...ö, additionalNotes: e.target.value }))}
            />
          </FormField>

          <FormField label="İstenen standart maddeler">
            <div className="flex flex-wrap gap-2">
              <Input
                className="min-w-48 flex-1"
                value={maddeMetni}
                onChange={(e) => setMaddeMetni(e.target.value)}
                onKeyDown={(e) => {
                  /** ⚠ Enter formu göndermiyor, madde ekliyor: üretim kredi harcar. */
                  if (e.key === "Enter") {
                    e.preventDefault();
                    maddeEkle();
                  }
                }}
                placeholder="Örn. Gizlilik"
              />
              <Button type="button" variant="secondary" onClick={maddeEkle}>
                Ekle
              </Button>
            </div>
            {girdi.standardClauses.length > 0 ? (
              <ul className="mt-2 flex flex-wrap gap-2">
                {girdi.standardClauses.map((madde, i) => (
                  <li key={`${madde}-${i}`}>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`${madde} maddesini çıkar`}
                      onClick={() =>
                        setGirdi((ö) => ({
                          ...ö,
                          standardClauses: ö.standardClauses.filter((_, j) => j !== i),
                        }))
                      }
                    >
                      {madde} ✕
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </FormField>

          <div className="flex flex-wrap justify-end gap-2">
            {/*
              ⚠ Ekranda taslak varken üretim ONAY İSTİYOR: ikinci üretim yeni bir kredi
              harcar ve yeni bir varyant doğurur. Onaysız çalışsaydı kullanıcı bir
              tıklamayla farkında olmadan ikinci kez ödeme yapardı.

              ⚠ Onay şeridi açıkken buradaki düğme HİÇ çizilmiyor: aynı adı taşıyan iki
              düğme kullanıcıya da ekran okuyucuya da hangisinin üreteceğini söylemez.
            */}
            {yenidenUretimOnayi ? null : acik ? (
              <Button type="button" onClick={() => setYenidenUretimOnayi(true)}>
                Yeniden üret
              </Button>
            ) : (
              <Button
                type="button"
                loading={uretimSuruyor}
                disabled={zorunluEksik || uretimSuruyor}
                onClick={() => uret.mutate()}
              >
                {uretimSuruyor ? "Taslak üretiliyor…" : "Taslak üret"}
              </Button>
            )}
          </div>

          {uretimHatasi ? (
            <p role="alert" data-testid="ai-taslak-uretim-hatasi" className="text-sm text-danger">
              {uretimHatasi}
            </p>
          ) : null}

          {yenidenUretimOnayi ? (
            <InfoBanner tone="warning">
              Yeni bir varyant üretmek AI kredisi harcar. Mevcut taslağınız silinmez; aşağıdaki
              varyant listesinde kalır.
              <div className="mt-2 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  loading={uretimSuruyor}
                  disabled={uretimSuruyor}
                  onClick={() => uret.mutate()}
                >
                  Yeni varyant üret
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setYenidenUretimOnayi(false)}
                >
                  Vazgeç
                </Button>
              </div>
            </InfoBanner>
          ) : null}
        </div>
      </Card>

      {/* ⚠ Canlı bölge: üretim uzun sürüyor ve ekran okuyucu kullanıcısı sessizlikte kalmamalı. */}
      <p aria-live="polite" className="sr-only">
        {uretimSuruyor ? "Taslak üretiliyor" : acik ? "Taslak hazır, inceleyin" : ""}
      </p>

      {varyantlar.length > 1 ? (
        <Card title="Bu oturumdaki varyantlar">
          <ul className="flex flex-col gap-2">
            {varyantlar.map((v, i) => (
              <li key={v.id}>
                <Button
                  type="button"
                  variant={v.id === acikId ? "secondary" : "ghost"}
                  data-testid={`ai-taslak-varyant-${v.id}`}
                  className="h-auto w-full flex-wrap justify-start gap-2 p-3 text-left font-normal"
                  onClick={() => {
                    setAcikId(v.id);
                    setOkundu(false);
                    setKabulHatasi(null);
                  }}
                >
                  <span className="text-sm text-fg">
                    {varyantlar.length - i}. varyant · {v.input.contractType || "—"}
                  </span>
                  <span className="ml-auto text-sm tabular-nums text-fg-muted">
                    {v.credits} kredi
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {acik ? (
        <Card>
          <h2
            ref={sonucRef}
            tabIndex={-1}
            className="mb-3 text-base font-semibold text-fg-heading outline-none"
          >
            Üretilen taslak
          </h2>

          {acik.status === "failed" ? (
            /** ⚠ Başarısızda kredi İADE edildi ve bunu söyleyen cümle şemadan geliyor. */
            <p role="alert" className="text-sm text-danger">
              {acik.errorCode
                ? CONTRACT_AI_DRAFT_ERROR_MESSAGES[acik.errorCode]
                : "Taslak üretilemedi."}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
                <Badge tone="neutral">{acik.model}</Badge>
                <span className="tabular-nums">{acik.credits} kredi</span>
              </div>

              {acik.output?.assumptions.length ? (
                <section>
                  <h3 className="text-sm font-medium text-fg-strong">AI'ın yaptığı varsayımlar</h3>
                  <ul className="mt-1 list-disc pl-5 text-sm text-fg-muted">
                    {acik.output.assumptions.map((v, i) => (
                      <li key={i}>{v}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {acik.output?.missingFields.length ? (
                <section>
                  <h3 className="text-sm font-medium text-fg-strong">Eksik bıraktığınız alanlar</h3>
                  <ul className="mt-1 list-disc pl-5 text-sm text-fg-muted">
                    {acik.output.missingFields.map((v, i) => (
                      <li key={i}>{v}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section className="flex flex-col gap-4" data-testid="ai-taslak-metin">
                {(acik.output?.document.clauses ?? []).map((madde, i) => (
                  <article key={i} className="flex flex-col gap-1">
                    {madde.title ? (
                      <h3 className="text-base font-semibold text-fg-heading">{madde.title}</h3>
                    ) : null}
                    {madde.blocks.map((blok, j) => (
                      <p key={j} className="text-sm text-fg">
                        {blok.text}
                      </p>
                    ))}
                  </article>
                ))}
              </section>

              <div className="flex flex-col gap-2 border-t border-border-subtle pt-3">
                {/*
                  ⚠ AÇIK ONAY: sunucu `confirmed: true` olmadan kabul etmiyor
                  (`AI_DRAFT_HUMAN_APPROVAL_REQUIRED_MESSAGE`) ve ekran o kapıyı ÖNDEN
                  gösteriyor. Kutucuk metni şemadan; ikinci bir cümle uydurulmadı.
                */}
                <Checkbox
                  checked={okundu}
                  onChange={(e) => setOkundu(e.target.checked)}
                  label={AI_DRAFT_HUMAN_APPROVAL_REQUIRED_MESSAGE}
                />
                <p className="text-sm text-fg-muted">
                  Kabul ettiğinizde sözleşme oluşturulur ve metni düzenleyebileceğiniz editöre
                  yönlendirilirsiniz.
                </p>
                {kabulHatasi ? (
                  <p role="alert" className="text-sm text-danger">
                    {kabulHatasi}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    loading={kabulEt.isPending}
                    /** ⚠ İstek sürerken KAPALI: ikinci tıklama ikinci sözleşme demek. */
                    disabled={!okundu || kabulEt.isPending}
                    onClick={() => {
                      setKabulHatasi(null);
                      kabulEt.mutate(acik.id);
                    }}
                  >
                    {kabulEt.isPending ? "Sözleşme oluşturuluyor…" : "Kabul et ve sözleşme oluştur"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={kabulEt.isPending}
                    onClick={() => {
                      setAcikId(null);
                      setOkundu(false);
                      setKabulHatasi(null);
                    }}
                  >
                    Taslağı kapat
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
}
